// ---------------------------------------------------------------------------
// C1-C4: AI Bar Hook — manages state, API calls, proactive polling
// ---------------------------------------------------------------------------
import { useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";
import type { AiBarMode } from "@/components/ai-bar/ai-bar";
import { useActiveTenantModule } from "@/hooks/use-active-tenant-module";
import { useDensity, type Density } from "@/providers/density-context";
import type {
  AiMessage,
  AssistantAdvicePayload,
  AssistantRuntimeResponse,
  AssistantTaskSummary,
} from "@ease/ai";
import type {
  CrmActionExecutionQueue,
  CrmActionExecutionQueueItem,
} from "@/hooks/use-crm-action-execution-queue";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AiResponse {
  mode: "ask" | "advise" | "do";
  content: string;
  actions?: Array<{ label: string; href?: string; action?: string }>;
  data?: Record<string, unknown>;
}

interface ProactiveSuggestion {
  id: string;
  message: string;
  action?: { label: string; href?: string };
}

type AiBarSuggestionModule = "crm";

const MAX_AI_BAR_HISTORY_MESSAGES = 12;
const CRM_DISABLED_AI_REASON = "AI assistant is not enabled for this tenant.";
const PROACTIVE_AI_BAR_POLLING_ENABLED = import.meta.env.VITE_AI_BAR_PROACTIVE_POLLING === "true";
const PROACTIVE_CRM_ACTION_QUEUE_PATH = "/crm/intelligence/action-recommendations/execution-queue?status=ready_for_execution&degrade=true";
const PROACTIVE_POLL_INTERVAL_MS = 60_000;
const PROACTIVE_POLL_BACKOFF_MS = 5 * 60_000;
const PROACTIVE_POLL_MAX_BACKOFF_MS = 30 * 60_000;

function toAiBarMode(mode: "ask" | "advise" | "do"): AiBarMode {
  return mode;
}

function buildRuntimeHistory(messages: AiMessage[]): AiMessage[] {
  return messages
    .filter((message) => message.role === "user" || message.role === "assistant")
    .slice(-MAX_AI_BAR_HISTORY_MESSAGES);
}

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "CRM action";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function buildActionQueueSuggestion(
  queue: CrmActionExecutionQueue | null | undefined,
  options: { density?: Density; module?: AiBarSuggestionModule } = {},
): ProactiveSuggestion | null {
  const nextAction: CrmActionExecutionQueueItem | null = queue?.nextExecutable ?? queue?.items[0] ?? null;
  if (!nextAction) {
    return null;
  }

  const density = options.density ?? "normal";
  const module = options.module ?? "crm";
  const totals = queue?.filteredTotals;
  const readyCount = totals?.readyForExecution ?? queue?.items.length ?? 1;
  const executableCount = totals?.materializableReady ?? 0;
  const manualOnlyCount = totals?.manualOnlyReady ?? 0;
  const bridgeBlockedCount = totals?.bridgeBlockedReady ?? 0;
  const countLabel = readyCount === 1 ? "1 accepted CRM action" : `${readyCount} accepted CRM actions`;
  const evidenceLabel = nextAction.observedOutcome ? " with outcome evidence" : "";
  const resolutionParts = [
    executableCount > 0 ? `${executableCount} executable` : null,
    manualOnlyCount > 0 ? `${manualOnlyCount} manual-only` : null,
    bridgeBlockedCount > 0 ? `${bridgeBlockedCount} missing bridge` : null,
  ].filter(Boolean).join(" · ");
  const resolutionLabel = resolutionParts ? ` (${resolutionParts})` : "";
  const nextActionLabel = prettyActionLabel(nextAction.actionRef);
  const actionHrefByModule: Record<AiBarSuggestionModule, string> = {
    crm: "/crm/today?tab=tasks&queue=ready",
  };

  if (density === "easy") {
    return {
      id: `${module}-action-queue:${nextAction.id}:${nextAction.recommendationId}:easy`,
      message: `${countLabel} ready in CRM. Start with ${nextActionLabel}.`,
      action: {
        label: "Open CRM queue",
        href: actionHrefByModule[module],
      },
    };
  }

  if (density === "power") {
    const powerParts = [
      `${countLabel} ready for CRM operator triage`,
      `${executableCount} executable`,
      `${manualOnlyCount} manual-only`,
      `${bridgeBlockedCount} missing bridge`,
      nextAction.observedOutcome ? "outcome evidence present" : "no outcome evidence yet",
      `next: ${nextActionLabel}`,
    ];

    return {
      id: `${module}-action-queue:${nextAction.id}:${nextAction.recommendationId}:power`,
      message: `${powerParts.join(" · ")}.`,
      action: {
        label: "Open CRM queue",
        href: actionHrefByModule[module],
      },
    };
  }

  return {
    id: `crm-action-queue:${nextAction.id}:${nextAction.recommendationId}`,
    message: `${countLabel} ready for operator triage${evidenceLabel}${resolutionLabel}. Next: ${nextActionLabel}.`,
    action: {
      label: "Open queue",
      href: actionHrefByModule[module],
    },
  };
}

function formatError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return "AI assistant is unavailable for this session. Refresh the workspace and sign in again if needed.";
    }

    if (error.status === 404) {
      return "AI assistant is not available in this environment yet.";
    }

    if (error.status === 429) {
      return "AI assistant is rate limited right now. Try again in a moment.";
    }

    if (error.status === 503 && error.errorCode === "AI_SERVICE_UNAVAILABLE") {
      return "AI assistant is not configured for this environment yet.";
    }

    if (error.status === 503) {
      return "AI assistant is temporarily unavailable right now. The upstream AI service did not respond.";
    }

    if (error.status >= 500) {
      return "AI assistant encountered a server error. Try again later.";
    }

    return error.message;
  }

  if (error instanceof DOMException && error.name === "AbortError") {
    return "AI assistant timed out before it could respond.";
  }

  return error instanceof Error ? error.message : "Unknown error";
}

function shouldDisableAiBar(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;

  // Auth failures and missing routes disable permanently
  if ([401, 403, 404].includes(error.status)) return true;

  // Missing AI_SERVICE binding disables permanently — no point retrying
  if (error.status === 503 && error.errorCode === "AI_SERVICE_UNAVAILABLE") return true;

  // Transient 503 (upstream AI service down) allows retries
  return false;
}

export const __aiBarInternals = {
  toAiBarMode,
  buildRuntimeHistory,
  buildActionQueueSuggestion,
  formatError,
  shouldDisableAiBar,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAiBar(enabled = true) {
  const [mode, setMode] = useState<AiBarMode>("idle");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [panelContent, setPanelContent] = useState<ReactNode>(null);
  const [lastResult, setLastResult] = useState<AiResponse | null>(null);
  const [advice, setAdvice] = useState<AssistantAdvicePayload | null>(null);
  const [activeTask, setActiveTask] = useState<AssistantTaskSummary | null>(null);
  const [currentSuggestion, setCurrentSuggestion] = useState<ProactiveSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runtimeUnavailableReason, setRuntimeUnavailableReason] = useState<string | null>(null);
  const lastPollRef = useRef(0);
  const pollBackoffUntilRef = useRef(0);
  const pollFailureCountRef = useRef(0);
  const historyRef = useRef<AiMessage[]>([]);
  const crmModule = useActiveTenantModule("crm");
  const density = useDensity();

  const clearInteractionState = useCallback(() => {
    setPanelContent(null);
    setLastResult(null);
    setAdvice(null);
    setActiveTask(null);
  }, []);

  // C4: Poll briefing priorities every 60s for proactive suggestions
  useEffect(() => {
    if (!enabled || crmModule.isLoading || !PROACTIVE_AI_BAR_POLLING_ENABLED) {
      setCurrentSuggestion(null);
      setIsPulsing(false);
      if (!enabled || crmModule.isLoading) {
        clearInteractionState();
        setError(null);
        setMode("idle");
        historyRef.current = [];
      }
      return;
    }

    if (!crmModule.isEnabled) {
      setCurrentSuggestion(null);
      setIsPulsing(false);
      clearInteractionState();
      setRuntimeUnavailableReason(CRM_DISABLED_AI_REASON);
      setError(null);
      setMode("idle");
      historyRef.current = [];
      return;
    }

    if (runtimeUnavailableReason === CRM_DISABLED_AI_REASON) {
      setRuntimeUnavailableReason(null);
    }

    const poll = async () => {
      if (runtimeUnavailableReason && runtimeUnavailableReason !== CRM_DISABLED_AI_REASON) {
        setCurrentSuggestion(null);
        setIsPulsing(false);
        return;
      }

      const now = Date.now();
      if (now < pollBackoffUntilRef.current) return;
      if (now - lastPollRef.current < PROACTIVE_POLL_INTERVAL_MS) return;
      lastPollRef.current = now;

      try {
        const response = await apiFetch<{ data: CrmActionExecutionQueue }>(PROACTIVE_CRM_ACTION_QUEUE_PATH, {
          skipSessionExpiredModal: true,
        });
        const suggestion = buildActionQueueSuggestion(response.data, {
          density: density.effective,
          module: "crm",
        });
        pollFailureCountRef.current = 0;
        pollBackoffUntilRef.current = 0;

        if (suggestion) {
          setCurrentSuggestion(suggestion);
          setIsPulsing(true);
          return;
        }

        setCurrentSuggestion(null);
        setIsPulsing(false);
      } catch (caughtError) {
        if (caughtError instanceof ApiError && (caughtError.status >= 400 || caughtError.status === 429)) {
          pollFailureCountRef.current += 1;
          const retryAfterMs = caughtError.retryAfterSeconds ? caughtError.retryAfterSeconds * 1000 : null;
          const exponentialBackoffMs = Math.min(
            PROACTIVE_POLL_BACKOFF_MS * (2 ** Math.max(0, pollFailureCountRef.current - 1)),
            PROACTIVE_POLL_MAX_BACKOFF_MS,
          );
          pollBackoffUntilRef.current = Date.now() + (retryAfterMs ?? exponentialBackoffMs);
        }
        setCurrentSuggestion(null);
        setIsPulsing(false);
      }
    };

    poll();
    const interval = setInterval(poll, PROACTIVE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [clearInteractionState, crmModule.isEnabled, crmModule.isLoading, density.effective, enabled, runtimeUnavailableReason]);

  // C2/C3: Handle user query submission
  const handleSubmit = useCallback(async (query: string) => {
    if (!enabled) {
      return;
    }

    if (crmModule.isLoading) {
      return;
    }

    if (!crmModule.isEnabled) {
      clearInteractionState();
      setMode("ask");
      setError(CRM_DISABLED_AI_REASON);
      return;
    }

    if (runtimeUnavailableReason && runtimeUnavailableReason !== CRM_DISABLED_AI_REASON) {
      clearInteractionState();
      setMode("ask");
      setError(runtimeUnavailableReason);
      return;
    }

    setIsProcessing(true);
    setIsPulsing(false);
    setError(null);
    setCurrentSuggestion(null);
    clearInteractionState();

    // Detect mode from query
    const isAdvise = query.toLowerCase().startsWith("should i");
    const isDo = query.toLowerCase().startsWith("do ") || query.toLowerCase().includes("do it for me");
    const detectedMode = isDo ? "do" : isAdvise ? "advise" : "ask";
    setMode(toAiBarMode(detectedMode));

    try {
      const result = await apiFetch<AssistantRuntimeResponse>("/crm/ai/runtime", {
        method: "POST",
        body: JSON.stringify({
          mode: detectedMode,
          query,
          history: buildRuntimeHistory(historyRef.current),
        }),
      });

      setMode(toAiBarMode(result.mode));
      setLastResult({
        mode: result.mode,
        content: result.content,
      });

      if (result.kind === "completion") {
        setAdvice(result.advice ?? null);
        historyRef.current = buildRuntimeHistory([
          ...historyRef.current,
          { role: "user", content: query },
          { role: "assistant", content: result.content },
        ]);
        return;
      }

      setActiveTask(result.task);
      historyRef.current = buildRuntimeHistory([
        ...historyRef.current,
        { role: "user", content: query },
        { role: "assistant", content: result.content },
      ]);
    } catch (caughtError) {
      const formattedError = formatError(caughtError);
      setLastResult(null);
      setAdvice(null);
      setActiveTask(null);
      setError(formattedError);

      if (shouldDisableAiBar(caughtError)) {
        setRuntimeUnavailableReason(formattedError);
        clearInteractionState();
        setIsPulsing(false);
        setMode("ask");
      }
    } finally {
      setIsProcessing(false);
    }
  }, [clearInteractionState, crmModule.isEnabled, crmModule.isLoading, enabled, runtimeUnavailableReason]);

  // C4: Handle pulse click (show proactive suggestion)
  const handlePulseClick = useCallback(() => {
    if (!enabled) {
      return;
    }

    if (runtimeUnavailableReason) {
      clearInteractionState();
      setMode("ask");
      setError(runtimeUnavailableReason);
      setIsPulsing(false);
      return;
    }

    if (currentSuggestion) {
      setIsPulsing(false);
      setMode("advise");
      // Suggestion will be rendered by the AI bar panel
    }
  }, [clearInteractionState, currentSuggestion, enabled, runtimeUnavailableReason]);

  return {
    mode,
    isProcessing,
    isPulsing,
    panelContent,
    lastResult,
    result: error ? null : lastResult,
    answer: error ? null : lastResult?.mode === "ask" ? lastResult.content : null,
    advice: error ? null : advice,
    activeTask: error ? null : activeTask,
    currentSuggestion,
    error,
    isRuntimeUnavailable: runtimeUnavailableReason !== null,
    runtimeUnavailableReason,
    handleSubmit,
    handlePulseClick,
    setMode,
    setPanelContent,
  };
}
